import { fetchAuthSession } from "aws-amplify/auth";
import { useRef } from "react";
import { buildWsUrl } from "../utils/wsUrl";

// Consumes the generic background-job progress channel
// (wss://.../api/v1/ws/job_progress/{job_id}/) that any long-running
// endpoint can enqueue work against instead of blocking the request — see
// api-regulify's core/common/job_manager.py. Mirrors useWebSocket.js's
// connection/keep-alive conventions so it's a drop-in sibling rather than a
// new pattern.
export function useJobProgress() {
  const wsRef = useRef(null);
  const keepAliveRef = useRef(null);

  const subscribeToJob = async (jobId, { onUpdate, onComplete, onError, requireAuth = true } = {}) => {
    let ws;
    try {
      const token = requireAuth ? (await fetchAuthSession()).tokens?.idToken?.toString() : "";

      ws = new WebSocket(buildWsUrl(`job_progress/${jobId}`, token || ""));
      wsRef.current = ws;

      ws.onopen = () => {
        keepAliveRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 20000);
      };

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed.type === "pong") return;

          if (parsed.type === "error") {
            onError?.(parsed.message || "Job failed");
            ws.close();
            return;
          }

          if (parsed.type === "job_update") {
            onUpdate?.(parsed);
            if (parsed.status === "completed") {
              onComplete?.(parsed.result);
              ws.close();
            } else if (parsed.status === "failed") {
              onError?.(parsed.error || parsed.message || "Job failed");
              ws.close();
            }
          }
        } catch (e) {
          console.error("Failed to parse job progress message", e);
        }
      };

      ws.onerror = () => {
        onError?.("Lost connection while tracking job progress.");
      };

      ws.onclose = () => {
        if (keepAliveRef.current) {
          clearInterval(keepAliveRef.current);
          keepAliveRef.current = null;
        }
      };
    } catch (e) {
      onError?.("Could not connect to job progress channel.");
    }
  };

  return { subscribeToJob };
}
